<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from '../../i18n'

const props = defineProps({ receipt: { type: Object, default: null }, loading: Boolean, error: { type: String, default: '' } })
const emit = defineEmits(['close'])
const { locale, t } = useI18n()
const panel = ref(null)
let previousFocus = null

function close() { emit('close') }
function keydown(event) {
  if (event.key === 'Escape') { event.preventDefault(); close(); return }
  if (event.key !== 'Tab' || !panel.value) return
  const focusable = [...panel.value.querySelectorAll('button,[href],input,select,[tabindex]:not([tabindex="-1"])')]
    .filter((item) => !item.disabled)
  if (!focusable.length) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}
function money(value) { return value == null ? '—' : new Intl.NumberFormat('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value) }
function number(value) { return value == null ? '—' : new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 3 }).format(value) }
function typeLabel(type) { return type === 'RETURN' ? t('receiptReturn') : type === 'ANOMALY' ? t('receiptAnomaly') : t('receiptSale') }
function dateTime(value, raw) {
  const code = ({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA'
  return value ? new Date(value).toLocaleString(code) : raw || '—'
}

onMounted(async () => {
  previousFocus = document.activeElement
  document.body.classList.add('drawer-open')
  await nextTick()
  panel.value?.querySelector('button')?.focus()
})
onBeforeUnmount(() => {
  document.body.classList.remove('drawer-open')
  previousFocus?.focus?.()
})
watch(() => props.receipt, async () => { await nextTick(); panel.value?.querySelector('button')?.focus() })
</script>

<template>
  <Teleport to="body">
    <div class="receipt-drawer-layer" @keydown="keydown">
      <button class="receipt-drawer-backdrop" type="button" :aria-label="t('close')" @click="close"></button>
      <aside ref="panel" class="receipt-drawer" role="dialog" aria-modal="true" aria-labelledby="receipt-drawer-title">
        <header class="receipt-drawer-header">
          <div>
            <p class="eyebrow">{{ t('receiptDetails') }}</p>
            <h2 id="receipt-drawer-title">{{ receipt?.receiptNumber != null ? `${t('receiptNumber')} №${receipt.receiptNumber}` : t('receiptDetails') }}</h2>
          </div>
          <button class="drawer-close" type="button" :aria-label="t('close')" @click="close">×</button>
        </header>
        <p v-if="loading" class="drawer-state">{{ t('loading') }}</p>
        <p v-else-if="error" class="notice error" role="alert">{{ error }}</p>
        <div v-else-if="receipt" class="receipt-detail-content">
          <section class="receipt-drawer-summary" :aria-label="t('receiptSummary')">
            <div class="receipt-total">
              <span>{{ t('totalAmount') }}</span>
              <strong>{{ money(receipt.totalAmountUah) }} <small>UAH</small></strong>
            </div>
            <div class="receipt-summary-facts">
              <p><span>{{ t('receiptId') }}</span><strong>{{ receipt.id }}</strong></p>
              <p><span>{{ t('positions') }}</span><strong>{{ receipt.itemCount }}</strong></p>
              <p><span>{{ t('receiptType') }}</span><strong class="receipt-type" :class="`receipt-type-${receipt.type.toLowerCase()}`">{{ typeLabel(receipt.type) }}</strong></p>
            </div>
          </section>

          <section class="receipt-drawer-section">
            <h3>{{ t('receiptMetadata') }}</h3>
            <dl class="receipt-signature">
              <div><dt>{{ t('receiptShiftNumber') }}</dt><dd>{{ receipt.sessionNumber ?? '—' }}</dd></div>
              <div><dt>{{ t('receiptNumber') }}</dt><dd>{{ receipt.receiptNumber ?? '—' }}</dd></div>
              <div><dt>{{ t('receiptDate') }}</dt><dd>{{ dateTime(receipt.createdAt, receipt.createdAtRaw) }}</dd></div>
              <div><dt>{{ t('paymentForm') }}</dt><dd>{{ receipt.paymentForms.join(', ') || '—' }}</dd></div>
            </dl>
          </section>

          <section class="receipt-drawer-section">
            <h3>{{ t('payments') }}</h3>
            <div class="receipt-payment-list">
              <article v-for="payment in receipt.payments" :key="payment.paymentId">
                <span>{{ payment.paymentFormName || `#${payment.paymentFormId ?? '—'}` }}</span>
                <strong>{{ money(payment.amountUah) }} UAH</strong>
              </article>
            </div>
          </section>

          <section class="receipt-drawer-section"><h3>{{ t('receiptPositionCount', { count: receipt.positions.length }) }}</h3>
            <article v-for="position in receipt.positions" :key="position.salesId" class="receipt-position-card">
              <header><strong>{{ position.productName || `${t('productCode')} ${position.productCode ?? '—'}` }}</strong><span>#{{ position.salesId }}</span></header>
              <dl>
                <div><dt>{{ t('pumpNumber') }}</dt><dd>{{ position.pumpNumber ?? '—' }}</dd></div>
                <div><dt>{{ t('nozzleNumber') }}</dt><dd>{{ position.nozzleNumber ?? '—' }}</dd></div>
                <div><dt>{{ t('tankNumberShort') }}</dt><dd>{{ position.tankNumber ?? '—' }}</dd></div>
                <div><dt>{{ t('productCode') }}</dt><dd>{{ position.productCode ?? '—' }}</dd></div>
                <div><dt>{{ t('uktzedCode') }}</dt><dd>{{ position.uktzedCode || '—' }}</dd></div>
                <div><dt>{{ t('receiptBarcode') }}</dt><dd class="receipt-barcodes">{{ position.barcodes?.join(', ') || '—' }}</dd></div>
                <div><dt>{{ t('preorderedAmount') }}</dt><dd>{{ number(position.preorderedAmount) }}</dd></div>
                <div><dt>{{ t('quantity') }}</dt><dd>{{ number(position.amount) }}</dd></div>
              </dl>
            </article>
          </section>
        </div>
        <footer class="receipt-drawer-footer">
          <button class="button button-secondary" type="button" @click="close">{{ t('close') }}</button>
        </footer>
      </aside>
    </div>
  </Teleport>
</template>
